import React from "react";
import { PageHeader } from "../../../../shared/components/PageHeader";
import { useGoToRequirementDefinitions } from "../../paths/helpers";
import { useRouteMatch } from "react-router-dom";
import {
  toWSDate,
  WSAvatar,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPanel,
  WSPill,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { useRequirementDefinitionQuery } from "../../../../query/requirementDefinitions/query/useRequirementDefinitionQuery";
import { WSQueries } from "../../../../query/WSQuery";
import {
  RequirementDefinitionStatus,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { useSignedDocumentTemplateQuery } from "../../../../query/signedDocuments/query/useSignedDocumentTemplateQuery";
import { RequirementInformation } from "../../components/RequirementInformation";
import { EngagementsTable } from "./EngagementsTable";
import { helloSignClient, helloSignPromise } from "../../../../utils/helloSign";
import { useEditRequirementDefinitionModal } from "../../components/EditRequirementDefinitionForm/useEditRequirementDefinitionModal";
import { useSharedFileSettingsQuery } from "../../../../query/sharedFile/query/useSharedFileSettingsQuery";
import { WSActionsMenuItem } from "@wingspanhq/fe-component-library/dist/lib/components/WSActions/WSActions";
import { useBackgroundChecksSettingsQuery } from "../../../../query/backgroundChecks/queries/useBackgroundChecksSettingsQuery";
import { SharedFileRequestJobType } from "@wingspanhq/shared-files/dist/lib/interfaces";
import { WayfinderWidget } from "../../../../shared/components/WayfinderWidget";
import { AIValidationWidget } from "../../../Requirements/components/AIValidationWidget";
import { RequirementContextType } from "../../../Requirements/types";
import { selectorIsSharedFileNotNoneContext } from "../../../Requirements/selectors/selectorIsSharedFileNotNoneContext";
import { ReportsInfoWidgetContent } from "../../../Requirements/modules/BackgroundChecks/components/ReportsInfoWidget";
import { IDENTITY_VERIFICATION_CHECKS } from "../../modules/IdentityVerification/createRequirementDefinition/CreateIdentityVerificationPartialForm";
import { ApprovalWidget } from "../../components/ApprovalWidget";
import { useRequirementDefinitionDefaultActions } from "../../hooks/useRequirementDefinitionDefaultActions";
import { useEngagementsListAllQuery } from "../../../../query/engagements/queries/useEngagementsListAllQuery";
import { RequirementDefinitionHistoryWidget } from "./useRequirementDefinitionHistoryWidget";
import { ENGAGEMENT_REQUIREMENTS_ROOT_PATH } from "../../paths/constants";
import { useMonitorSettingsQuery } from "../../../../query/monitorSettings/query/useMonitorSettingsQuery";

export const RouteRequirementDetails: React.FC = () => {
  const match = useRouteMatch<{ requirementId: string }>();
  const goToRequirementDefinitions = useGoToRequirementDefinitions();
  const { requirementId } = match.params;
  const queryRequirementDefinition =
    useRequirementDefinitionQuery(requirementId);
  const requirementDefinitionDefaultActions =
    useRequirementDefinitionDefaultActions();
  const queryEngagementsList = useEngagementsListAllQuery();

  const editRequirementDefinitionModal = useEditRequirementDefinitionModal();
  const signedDocumentTemplateQuery = useSignedDocumentTemplateQuery(
    queryRequirementDefinition.data?.dataSourceId as string,
    {
      enabled:
        queryRequirementDefinition.data?.requirementType ===
          RequirementType.Signature &&
        !!queryRequirementDefinition.data?.dataSourceId
    }
  );
  const sharedFileSettingsQuery = useSharedFileSettingsQuery(
    queryRequirementDefinition.data?.dataSourceId as string,
    {
      enabled:
        queryRequirementDefinition.data?.requirementType ===
          RequirementType.SharedFile &&
        !!queryRequirementDefinition.data?.dataSourceId
    }
  );

  const backgroundChecksSettingsQuery = useBackgroundChecksSettingsQuery(
    queryRequirementDefinition.data?.dataSourceId as string,
    {
      enabled:
        queryRequirementDefinition.data?.requirementType ===
          RequirementType.BackgroundCheck &&
        !!queryRequirementDefinition.data?.dataSourceId
    }
  );

  const monitorSettingsQuery = useMonitorSettingsQuery(
    queryRequirementDefinition.data?.dataSourceId as string,
    {
      enabled:
        queryRequirementDefinition.data?.requirementType ===
          RequirementType.InsuranceCoverage &&
        !!queryRequirementDefinition.data?.dataSourceId
    }
  );

  return (
    <WSPage>
      <WSQueries queries={{ queryRequirementDefinition, queryEngagementsList }}>
        {({ queryRequirementDefinitionData, queryEngagementsListData }) => {
          const loadingByType: Record<RequirementType, boolean> = {
            [RequirementType.Signature]:
              signedDocumentTemplateQuery.isInitialLoading,
            [RequirementType.SharedFile]:
              sharedFileSettingsQuery.isInitialLoading,
            [RequirementType.BackgroundCheck]:
              backgroundChecksSettingsQuery.isInitialLoading,
            [RequirementType.IdentityVerification]: false,
            [RequirementType.InsuranceCoverage]:
              monitorSettingsQuery.isInitialLoading,
            [RequirementType.Registration]: false,
            [RequirementType.TaxVerification]: false,
            [RequirementType.PayoutMethod]: false
          };

          if (loadingByType[queryRequirementDefinitionData.requirementType]) {
            return null;
          }

          const onPreviewByType: Record<
            RequirementType,
            undefined | (() => Promise<void>)
          > = {
            [RequirementType.Signature]: async () => {
              let template = signedDocumentTemplateQuery.data;
              if (!signedDocumentTemplateQuery.isSuccess) return;
              if (!template?.viewUrl) {
                console.error("No HS view URL provided");
                return;
              }

              helloSignClient.open(template.viewUrl);

              await helloSignPromise(); // wait for close
              await signedDocumentTemplateQuery.refetch(); // refetching to enable re-opening the window. view URLs seems to be one-time
            },
            [RequirementType.SharedFile]: undefined,
            [RequirementType.BackgroundCheck]: undefined,
            [RequirementType.IdentityVerification]: undefined,
            [RequirementType.InsuranceCoverage]: undefined,
            [RequirementType.Registration]: undefined,
            [RequirementType.TaxVerification]: undefined,
            [RequirementType.PayoutMethod]: undefined
          };

          const validationJobs =
            sharedFileSettingsQuery.data?.jobs?.filter(
              job => job.type === SharedFileRequestJobType.Validation
            ) || [];

          const aiSupportByType: Record<RequirementType, boolean> = {
            [RequirementType.Signature]: false,
            [RequirementType.SharedFile]: selectorIsSharedFileNotNoneContext(
              sharedFileSettingsQuery?.data?.contextType
            ),
            [RequirementType.BackgroundCheck]: false,
            [RequirementType.IdentityVerification]: true,
            [RequirementType.InsuranceCoverage]: true,
            [RequirementType.Registration]: false,
            [RequirementType.TaxVerification]: false,
            [RequirementType.PayoutMethod]: false
          };

          const statusPill: Record<
            RequirementDefinitionStatus,
            React.ReactNode
          > = {
            [RequirementDefinitionStatus.Inactive]: (
              <WSPill theme="neutral" text="Archived" icon={false} />
            ),

            [RequirementDefinitionStatus.Active]: (
              <WSPill theme="success" text="Ready" icon={false} />
            )
          };

          const widgetsByType: Record<RequirementType, React.ReactNode> = {
            [RequirementType.Signature]: null,
            [RequirementType.SharedFile]: selectorIsSharedFileNotNoneContext(
              sharedFileSettingsQuery?.data?.contextType
            ) ? (
              <>
                <AIValidationWidget
                  contextType={RequirementContextType.Payer}
                  validationItems={validationJobs}
                />
                <WayfinderWidget />
              </>
            ) : null,
            [RequirementType.BackgroundCheck]: backgroundChecksSettingsQuery
              .data?.package?.searches ? (
              <ReportsInfoWidgetContent
                searches={backgroundChecksSettingsQuery.data?.package?.searches}
              />
            ) : null,
            [RequirementType.IdentityVerification]: (
              <>
                <AIValidationWidget
                  contextType={RequirementContextType.Payer}
                  validationItems={IDENTITY_VERIFICATION_CHECKS}
                />
                <WayfinderWidget />
              </>
            ),
            [RequirementType.InsuranceCoverage]: (
              <>
                {/*
                // TODO: change to specific validations
                <AIValidationWidget
                  contextType={RequirementContextType.Payer}
                  validationItems={IDENTITY_VERIFICATION_CHECKS}
                />
                */}
                <WayfinderWidget />
              </>
            ),
            [RequirementType.Registration]: null,
            [RequirementType.TaxVerification]: null,
            [RequirementType.PayoutMethod]: null
          };

          const openPreview =
            onPreviewByType[queryRequirementDefinitionData.requirementType];

          const showEditRequirement = !selectorIsSharedFileNotNoneContext(
            sharedFileSettingsQuery?.data?.contextType
          );

          return (
            <WSElement>
              <PageHeader
                title="Requirements"
                rootPath={ENGAGEMENT_REQUIREMENTS_ROOT_PATH}
                breadcrumbs={[
                  {
                    label: "Requirements",
                    onClick: () => {
                      goToRequirementDefinitions(
                        queryRequirementDefinitionData.status
                      );
                    }
                  },
                  {
                    label: queryRequirementDefinitionData.name
                  }
                ]}
                buttons={
                  openPreview
                    ? [
                        {
                          label: "Preview requirement",
                          onClick: openPreview
                        }
                      ]
                    : undefined
                }
                fetchMenuItems={() =>
                  requirementDefinitionDefaultActions({
                    requirementDefinition: queryRequirementDefinitionData,
                    engagements: queryEngagementsListData,
                    onDelete: requirementDefinition => {
                      goToRequirementDefinitions(requirementDefinition.status);
                    }
                  })
                }
                menuItems={
                  [
                    ...(openPreview
                      ? [
                          {
                            icon: "view",
                            label: "Preview requirement",
                            onClick: openPreview
                          }
                        ]
                      : []),
                    ...(showEditRequirement
                      ? [
                          {
                            icon: "edit",
                            label: "Edit requirement",
                            onClick: () =>
                              editRequirementDefinitionModal.open({
                                requirementDefinitionId: requirementId
                              })
                          }
                        ]
                      : [])
                  ] as WSActionsMenuItem[]
                }
              />

              <WSFlexBox.CenterY mt="L" mb="2XL" gap="M">
                <WSText.Heading3 singleLine>
                  {queryRequirementDefinitionData.name}
                </WSText.Heading3>
                {aiSupportByType[
                  queryRequirementDefinitionData.requirementType
                ] ? (
                  <WSAvatar
                    size="L"
                    type="icon"
                    icon="compass"
                    format="square"
                    colorBackground="garnet"
                  />
                ) : null}

                {statusPill[queryRequirementDefinitionData.status]}
                <WSText.ParagraphXs color="gray500">
                  Last modified{" "}
                  {
                    toWSDate(queryRequirementDefinitionData.updatedAt)
                      .formattedValue
                  }
                </WSText.ParagraphXs>
              </WSFlexBox.CenterY>

              <WSGrid>
                <WSGrid.Item span={{ s: "12", l: "8" }}>
                  <WSList gap="XL">
                    <WSElement>
                      <WSSectionToolbar title="Requirement information" />
                      <WSPanel mt="M">
                        <RequirementInformation
                          requirementDefinition={queryRequirementDefinitionData}
                          singedDocumentTemplate={
                            signedDocumentTemplateQuery?.data
                          }
                          sharedFileSettings={sharedFileSettingsQuery?.data}
                          backgroundChecksSettings={
                            backgroundChecksSettingsQuery?.data
                          }
                          monitorSettings={monitorSettingsQuery?.data}
                        />
                      </WSPanel>
                    </WSElement>

                    <WSElement>
                      <WSSectionToolbar
                        mb="M"
                        title="Requirement in engagement types"
                      />
                      <EngagementsTable requirementId={requirementId} />
                    </WSElement>
                  </WSList>
                </WSGrid.Item>
                <WSGrid.Item span={{ s: "12", l: "4" }}>
                  <WSList gap="L">
                    <ApprovalWidget
                      reviewStrategy={
                        queryRequirementDefinitionData.reviewStrategy
                      }
                    />

                    {
                      widgetsByType[
                        queryRequirementDefinitionData.requirementType
                      ]
                    }

                    <RequirementDefinitionHistoryWidget
                      requirementDefinitionId={requirementId}
                    />
                  </WSList>
                </WSGrid.Item>
              </WSGrid>
            </WSElement>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
